// Home dashboard — adaptive signal-driven bento (home-bento-v3).
//
// Framework-agnostic pure logic for the Home ("command") dashboard redesign:
//   1. classify a chip group's SIGNAL LEVEL from the data it already has,
//   2. fold that together with the fetch lifecycle into a widget STATUS that
//      NEVER masks a failed/pending fetch as a calm "all clear",
//   3. partition chips into the loud (attention) set shown at rest vs the muted
//      (zero) set revealed on hover/expand,
//   4. compose the bento by ordering widgets attention-first (layout engine).
//
// No React, no DOM — unit-tested under src/lib/home/homeSignals.test.ts so the
// compression rules stay a regression-guarded contract (docs/REGRESSIONS.md).

/** Signal a widget reports purely from its own data. */
export type SignalLevel = 'attention' | 'active' | 'quiet'

/** Widget status = signal level folded with the data-fetch lifecycle. */
export type WidgetStatus = SignalLevel | 'loading' | 'error'

/** One count chip inside a Home signal widget (HR Center, Work queue, Accounting snapshot). */
export interface SignalChip {
  key: string
  value: number
  /**
   * True when a non-zero value here means "needs action" (amber/red semantics),
   * false for purely informational counts (e.g. "Active jobs"). Drives whether a
   * non-zero group reads as `attention` vs merely `active`.
   */
  attention?: boolean
}

/** Fetch lifecycle for a widget's underlying data source. */
export interface FetchLifecycle {
  /** A request is in flight. */
  loading: boolean
  /** At least one request has SUCCEEDED — we hold real (possibly stale) data. */
  loaded: boolean
  /** The most recent request FAILED. */
  error: boolean
}

/**
 * Classify a chip group's signal level from its values alone (pure).
 * - `attention` — at least one action-needing chip is non-zero.
 * - `active`    — has non-zero data but nothing flagged as needing action.
 * - `quiet`     — every chip is zero.
 */
export function classifyChips(chips: SignalChip[]): SignalLevel {
  if (chips.some((c) => c.attention && c.value > 0)) return 'attention'
  if (chips.some((c) => c.value > 0)) return 'active'
  return 'quiet'
}

/**
 * Fold chip data together with the fetch lifecycle into a widget status.
 *
 * Guardrail (spec compression rule 1): a `quiet` all-clear collapse is only ever
 * returned for a SUCCESSFUL fetch of all-zeros. A failed fetch — or a fetch that
 * has never succeeded — returns `error` / `loading`, never `quiet`, so zeros
 * caused by a broken request are never rendered as calm.
 */
export function widgetStatus(chips: SignalChip[], life: FetchLifecycle): WidgetStatus {
  // A never-succeeded in-flight request is loading (skeleton), regardless of a
  // transient error flag from a prior attempt.
  if (life.loading && !life.loaded) return 'loading'
  // Failed with no successful data to fall back on → surface the error, don't fake calm.
  if (life.error && !life.loaded) return 'error'
  // Never loaded, not loading, no error yet (e.g. the very first render before the
  // fetch effect fires): optimistically show the loading skeleton, NOT "all clear"
  // and NOT an error — a request is about to run.
  if (!life.loaded) return 'loading'
  return classifyChips(chips)
}

/** Loud (value > 0) chips shown at rest vs muted (zero) chips revealed on expand. */
export interface ChipPartition<T extends { value: number }> {
  loud: T[]
  muted: T[]
}

/**
 * Split chips into the loud set (non-zero — shown at rest) and the muted set
 * (zero — revealed on hover/expand). Input order is preserved within each set.
 */
export function partitionChips<T extends { value: number }>(chips: T[]): ChipPartition<T> {
  const loud: T[] = []
  const muted: T[] = []
  for (const c of chips) (c.value > 0 ? loud : muted).push(c)
  return { loud, muted }
}

/**
 * Order chips for the home-bento-v3 click-open breakdown popup: loud (non-zero)
 * chips first so the items needing attention lead, muted (zero) chips trailing —
 * EVERY chip is present (including zeros) so the full breakdown lives one click
 * away. A pure layout contract for the v3 signal-badge popover, which replaced
 * the v1 hover-reveal (no chip may be dropped; loud-then-muted, stable within
 * each set). Built on partitionChips so the loud/muted split stays single-source.
 */
export function orderChipsForPopup<T extends { value: number }>(chips: T[]): T[] {
  const { loud, muted } = partitionChips(chips)
  return [...loud, ...muted]
}

/** A dashboard widget registered with the layout engine. */
export interface WidgetEntry {
  id: string
  type: 'kpi' | 'chips' | 'table' | 'agenda' | 'ranked-list' | 'health'
  status: WidgetStatus
  /** Nav-hidden / capability-gated widgets are dropped from the composition. */
  hidden?: boolean
}

// Attention rises to the top; a broken widget sits just under it (visible, not
// buried); informational/active next; still-loading below; calm "quiet" last.
const STATUS_RANK: Record<WidgetStatus, number> = {
  attention: 0,
  error: 1,
  active: 2,
  loading: 3,
  quiet: 4,
}

/**
 * Compose the bento: drop hidden widgets, then order attention-first by status
 * (stable within a tier — original registration order breaks ties). Pure, so the
 * layout is a testable function of the registry rather than a hardcoded 8-slot list.
 */
export function composeBento(entries: WidgetEntry[]): WidgetEntry[] {
  return entries
    .filter((e) => !e.hidden)
    .map((e, i) => ({ e, i }))
    .sort((a, b) => STATUS_RANK[a.e.status] - STATUS_RANK[b.e.status] || a.i - b.i)
    .map((x) => x.e)
}
